// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type {
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  getCartItemWeight,
  getProductImages,
  hideStrikethroughPrice,
} from '@iheartjane/menu';
import type { Spacing } from '@iheartjane/reefer';
import {
  Box,
  Flex,
  Image,
  JaneGoldIcon,
  TagIcon,
  Typography,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { useKioskUser } from '@jane/kiosk/data-access';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  useGetProductPrices,
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  customCategoryLabel,
  customLineageLabel,
  isSupportedStoreSpecial,
  potencyDescription,
} from '@jane/kiosk/util';
import type { AlgoliaPriceId, AlgoliaProduct } from '@jane/search/data-access';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { MenuProduct, Store, StoreSpecial } from '@jane/shared/types';

import { HighlightedText } from '../HighlightedText/HighlightedText';
import { StarRating } from '../StarRating/StarRating';
import { cartRowPrice } from './cartRowPrice';
import type { CartRowProduct } from './cartRowProduct';

interface ProductRowPropsBase {
  janeGoldTagLabel?: string | null;
  onProductImageClick?: (product: CartRowProduct) => void;
  priceId?: AlgoliaPriceId | PriceId;
  product: AlgoliaProduct | MaterializedMenuProduct | CartRowProduct;
  productInstance?: SmartSortProduct<MaterializedMenuProduct>;
  /** In the context of showing search results, the substring to highlight in a Product's listing */
  searchTerm?: string;
  showCategory?: boolean;
  showPotency?: boolean;
  showPrice?: boolean;
  showRating?: boolean;
}

interface ProductRowWithActionsProps extends ProductRowPropsBase {
  actions: ReactNode | ReactNode[];
  onClick?: never;
}

interface ClickableProductRow extends ProductRowPropsBase {
  actions?: never;
  onClick?: (product: SmartSortProduct<MaterializedMenuProduct>) => void;
}

/**
 * `actions` and `onClick` are mutually exclusive through these props
 * because of an axe accessibility rule about nested interactive controls.
 * https://dequeuniversity.com/rules/axe/4.4/nested-interactive?application=axeAPI
 */
export type ProductRowProps = ClickableProductRow | ProductRowWithActionsProps;

/**
 * carts-v3 rule 2: discriminate the cart-row arm of the prop union. Keys on
 * `count` (not `price_id`) because v3 CartProduct also has `price_id`, so the
 * bare `'price_id' in product` guard would mis-narrow a v3 product to the
 * legacy shape. Only cart products (v1 PendingCartProduct and v3 CartProduct)
 * carry `count`; AlgoliaProduct and MaterializedMenuProduct do not.
 */
const isCartRowProduct = (
  product: AlgoliaProduct | MaterializedMenuProduct | CartRowProduct
): product is CartRowProduct => 'count' in product;

const InViewFlex = styled(InView)<{ mb?: Spacing }>(({ mb }) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: mb,
}));

export const ProductRow = ({
  actions = null,
  productInstance,
  janeGoldTagLabel,
  onClick,
  onProductImageClick,
  priceId,
  product,
  searchTerm,
  showCategory = false,
  showPotency = false,
  showPrice = true,
  showRating = true,
}: ProductRowProps) => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  // TODO(carts-v3): rule 3 — getProductImages/useGetProductPrices/
  // potencyDescription/useShouldKioskShowGold require the legacy io-ts/Algolia
  // union and are retired in their own follow-ups (read precomputed v3 fields).
  // This is the same `product` (cart OR non-cart) re-typed to satisfy those
  // legacy util signatures — a single backward-cast chokepoint, not a separate
  // product. See docs/specs/carts-v3/2026-06-11-migration-map-design.md.
  const productForLegacyUtils = product as
    AlgoliaProduct | MaterializedMenuProduct | MenuProduct;
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: userData } = useKioskUser(undefined, {
    enabled: !!isCartRowProduct(product),
  });
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);

  const isSupportedSpecial =
    specials
      .filter(({ id }) => product.special_id === id)
      .filter((special) => isSupportedStoreSpecial(special, true)).length > 0;

  const { images: productImages } = useMemo(
    () => getProductImages(productForLegacyUtils),
    [productForLegacyUtils]
  );
  // carts-v3 rule 3: cart rows read precomputed price fields via cartRowPrice;
  // search/menu (non-cart) products keep the useGetProductPrices hook.
  const getProductPrice = useGetProductPrices(productForLegacyUtils, specials);
  const {
    discountPrice,
    priceId: formattedPriceId,
    unitPrice,
  } = isCartRowProduct(product)
    ? cartRowPrice(product, priceId, isSupportedSpecial)
    : getProductPrice(priceId);

  const shouldShowGold = useShouldKioskShowGold();

  const quantity = isCartRowProduct(product) ? product.count : 1;
  const { defaultSpacing, isIpad } = useKioskSpacing();

  const productKindLabel = customCategoryLabel({
    kind: product.kind,
    store,
  });

  const showWeight =
    isCartRowProduct(product) && product.price_id !== 'each' && !product.amount;
  // carts-v3 rule 3: getCartItemWeight wants ProductType for `type`, but v3
  // CartProduct types it as the wider `string`, so the narrowed CartRowProduct
  // cannot satisfy GetCartItemWeightProps without a cast. Only price_id/type
  // are read, both present on CartRowProduct; this is a guarded cart-only path.
  const productWeight = showWeight
    ? `${getCartItemWeight(product as Parameters<typeof getCartItemWeight>[0])}`
    : '';

  return (
    <InViewFlex
      aria-label={`${onClick ? 'view ' : ''}${product.name} detail`}
      mb={defaultSpacing}
      onClick={
        onClick ? () => productInstance && onClick(productInstance) : undefined
      }
      onChange={(inView: boolean) => {
        if (inView && productInstance) {
          productInstance.impress();
        }
      }}
      triggerOnce
      threshold={0.5}
      delay={1000}
    >
      <Flex
        alignItems="center"
        flexDirection="row"
        onClick={
          isCartRowProduct(product) && !!onProductImageClick
            ? () => onProductImageClick(product)
            : undefined
        }
        ariaLabel={`cart product ${product.product_id} info`}
      >
        <Box border="transparent-black-five" borderRadius="sm">
          <Image
            altText={productImages[0].imageAlt}
            borderRadius="15px"
            height={isIpad ? '88px' : '120px'}
            src={productImages[0].imageSrc}
            width={isIpad ? '88px' : '120px'}
            sizes="thumbnail"
            responsive
          />
        </Box>
        <Flex flexDirection="column" ml={defaultSpacing}>
          <HighlightedText highlight={searchTerm} variant="body-semibold">
            {product.name}
          </HighlightedText>
          {product.brand && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {product.brand}
            </HighlightedText>
          )}
          {product.kind && productKindLabel && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {productKindLabel}
            </HighlightedText>
          )}
          {showCategory && product.category && (
            <HighlightedText color="grays-mid" highlight={searchTerm}>
              {customLineageLabel({
                store,
                lineage: product.category,
              })}
            </HighlightedText>
          )}
          {showPotency &&
            potencyDescription(productForLegacyUtils, formattedPriceId) && (
              <HighlightedText color="grays-mid" highlight={searchTerm}>
                {potencyDescription(productForLegacyUtils, formattedPriceId)}
              </HighlightedText>
            )}
          {isSupportedSpecial && product.special_title && (
            <Flex alignItems="center">
              <TagIcon size="sm" color="sunset" />
              <HighlightedText ml={4} color="sunset" highlight={searchTerm}>
                {product.special_title}
              </HighlightedText>
            </Flex>
          )}
          {shouldShowGold && janeGoldTagLabel && (
            <>
              <Flex alignItems="center">
                <JaneGoldIcon size="sm" />
                <HighlightedText
                  ml={4}
                  color="grays-black"
                  highlight={searchTerm}
                >
                  {janeGoldTagLabel}
                </HighlightedText>
              </Flex>
              {isCartRowProduct(product) &&
                !!product.brand_special_has_segment_rules &&
                !userData?.id && (
                  <Flex alignItems="center">
                    <Typography color="grays-mid">
                      (Sign in for eligibility)
                    </Typography>
                  </Flex>
                )}
            </>
          )}
          {!!product.review_count &&
            product.review_count >= 5 &&
            !!product.aggregate_rating &&
            showRating &&
            store.product_reviews_enabled && (
              <StarRating
                aggregateRating={product.aggregate_rating}
                ratingSuffix=""
                reviewCount={product.review_count}
              />
            )}
        </Flex>
      </Flex>
      <Flex
        flexDirection="row"
        alignItems={actions ? 'center' : 'start'}
        height="100%"
      >
        {showPrice && (
          <Flex alignItems="flex-end" flexDirection="column" grow={1}>
            {!!discountPrice && (
              <Typography color="sunset" variant="body-semibold">
                {formatCurrency(quantity * discountPrice)}
              </Typography>
            )}
            {!!unitPrice &&
              (!discountPrice ||
                !hideStrikethroughPrice(store?.state || '')) && (
                <Typography
                  color={discountPrice ? 'grays-mid' : undefined}
                  variant={discountPrice ? undefined : 'body-semibold'}
                  strikeThrough={!!discountPrice}
                >
                  {formatCurrency(quantity * unitPrice)}
                </Typography>
              )}
            {showWeight && <Typography>{productWeight}</Typography>}
          </Flex>
        )}
        {actions}
      </Flex>
    </InViewFlex>
  );
};
